<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t('Custom Conversions') }}
    </div>
    <div class="settings-content">
      <div class="settings-block">
        <div class="settings-block__title">
          <div class="settings-block__title-text">
            {{ t('Custom Conversions: {0} of 20', [activeCustomEvents.length]) }}
          </div>
          <div class="read-guide">
            <a
              :href="`https://docs.bemob.com/${locale}/custom-conversions`"
              class="read-guide"
              target="_blank"
            >
              <ui-tooltip-icon
                preset="help"
                :text="t('Learn more about the custom conversions tracking with BeMob.')"
              />
              <span class="read-guide ml-6">
                {{ t('Read Guide') }}
              </span>
            </a>
            <ui-button
              class="ml-8"
              :text="t('Add Event')"
              icon="fas fa-plus"
              planLimits="customConversionsAmount"
              button-style="accent"
              @click="openCreateCustomConversions(null)"
            />
          </div>
        </div>

        <ui-checkbox
          :label="t('Show Archived')"
          v-model="showArchived"
          class="show-archived"
        />

        <ui-table
          :headers="headers"
          :items="filteredRows"
          :loading="loading"
          :useSearch="false"
          :virtualize="false"
          :rowsPerPageCount="10"
          :always-show-pagination="allRows.length > 10"
          class="main-table mt-8"
        >
          <template #item.name="{ item }">
            <div :class="{ archived: item.status === 'archived' }">
              {{ item.name }}
            </div>
          </template>

          <template #item.values="{ item }">
            <div :class="{ archived: item.status === 'archived' }">
              <div v-if="item.values && item.values.length > 0" class="onePlus-wrp">
                <div class="onePlus-text">
                  {{ item.values[0] }}
                </div>
                <v-tooltip
                  v-if="item.values.length > 1"
                  :color="$colors.white"
                  content-class="onePlus-tooltip"
                  location="top"
                >
                  <template #activator="{ props }">
                    <div class="onePlus-text onePlus-text--count ml-8" v-bind="props">
                      {{ `+${item.values.length - 1}` }}
                    </div>
                  </template>
                  <div class="onePlus-text-wrp">
                    <div v-for="itemInner in item.values.slice(1)" class="onePlus-text">
                      {{ itemInner }}
                    </div>
                  </div>
                </v-tooltip>
              </div>
            </div>
          </template>

          <template #item.actions="{ item }">
            <div class="main-table__action-buttons">
              <button
                :style="{ color: item.status === 'active' ? $colors.red : $colors.green }"
                class="main-table__action-button"
                type="button"
                @click="archiveRestoreConfirm(item)"
              >
                <ui-icon
                  :color="item.status === 'active' ? $colors.red : $colors.green"
                  class="mr-4"
                  :icon="`fas ${item.status === 'active' ? 'fa-archive' : 'fa-undo'}`"
                />
                {{ item.status === 'active' ? t('Archive') : t('Restore') }}
              </button>
              <button
                class="main-table__action-button"
                type="button"
                @click="openCreateCustomConversions(item)"
              >
                <ui-icon :color="$colors.green" class="mr-4" icon="fas fa-pen" />
                {{ t('Edit') }}
              </button>

              <button
                class="main-table__action-button"
                type="button"
                @click="generateLinks(item)"
                :style="{ color: $colors.bluedeep }"
              >
                <ui-icon :color="$colors.bluedeep" class="mr-4" icon="fas fa-link" />
                {{ t('Generate Links') }}
              </button>
            </div>
          </template>

          <template #no-data>
            <ui-not-found />
          </template>
        </ui-table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from '@/store';
import { useColors, useI18n, useReportParams } from '@/composables';
import $emitter from '@/services/emitter';
import { getSupportedMetricsApiRequest } from '@/services/api/report-api';
import {
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE,
} from '@/services/constants/report';
import {
  getCustomConversionsApiRequest,
  putCustomConversionApiRequest,
} from '@/services/api/custom-conversions-api';

type CustomConversionRow = {
  id: string | number;
  name: string;
  values: string[];
  status: 'active' | 'archived' | string;
  slug?: string;
  [key: string]: unknown;
};

const store = useStore();
const reportParamsApi = useReportParams();
const route = useRoute();
const { t, locale } = useI18n();
const $colors = useColors();

const loading = ref(true);
const showArchived = ref(false);

const headers = computed(() => [
  {
    title: t('Custom Event'),
    align: 'left',
    value: 'name',
    sortable: true,
  },
  {
    title: t('Parameter Values'),
    align: 'left',
    value: 'values',
    sortable: false,
  },
  {
    title: '',
    align: 'left',
    value: 'actions',
    sortable: false,
  },
]);

const allRows = computed(
  () => Object.values(store.customEvents) as CustomConversionRow[],
);

const filteredRows = computed(() => {
  if (showArchived.value) return allRows.value;
  return allRows.value.filter((item: CustomConversionRow) => item.status === 'active');
});

const openCreateCustomConversions = (item: CustomConversionRow | null) => {
  $emitter.emit('open-dialog', { name: 'CreateCustomConversions', data: item });
};

const activeCustomEvents = computed(
  () =>
    Object.values(store.customEvents).filter(
      (item: unknown) => (item as CustomConversionRow).status !== 'archived',
    ) as CustomConversionRow[],
);

const load = async () => {
  const data = await getCustomConversionsApiRequest();
  store.setCustomEvents(data);
};

onMounted(async () => {
  store.setLoading(true);
  try {
    await load();
  } finally {
    loading.value = false;
    store.setLoading(false);
  }
});

const archiveRestoreConfirm = (item: CustomConversionRow) => {
  if (item.status !== 'active') {
    archiveRestore(item);
    return;
  }

  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t(
        'All selected items will be archived if you press OK. You can restore them anytime.',
      ),
      onConfirm: () => archiveRestore(item),
    },
  });
};

const archiveRestore = (item: CustomConversionRow) => {
  const newStatus = item.status === 'active' ? 'archived' : 'active';
  return putCustomConversionApiRequest({
    ...item,
    status: newStatus,
  })
    .then(() => {
      getCustomConversionsApiRequest().then(data => {
        if (item.status === 'active' && item.slug) {
          store.omitReportsColumns(
            [
              METRICS_CUSTOM_EVENT,
              METRICS_CUSTOM_EVENT_REVENUE,
              METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
            ].map((prefix: string) => `${prefix}${item.slug}`),
          );
        }

        store.setCustomEvents(data);
      });

      const text =
        item.status === 'active'
          ? t('The event archived successfully')
          : t('The event restored successfully');
      $emitter.emit('toast-success', text);

      getSupportedMetricsApiRequest().then(metrics => {
        store.setMetrics(metrics);
      });
    })
    .catch((error: any) => {
      $emitter.emit('toast-error', t(error.response.data.message));
      return Promise.reject(error);
    });
};

const generateLinks = (data: CustomConversionRow) => {
  $emitter.emit('open-dialog', { name: 'GenerateLinks', data });
};
</script>

<style lang="scss" scoped>
.read-guide {
  text-decoration: none;
  color: var(--color-black);
  display: flex;
  white-space: nowrap;
  align-items: center;
  .v-tooltip {
    line-height: 0;
  }
}

.main-table {
  :deep(.ui-table__inner) {
    table-layout: fixed;
    min-width: 450px;

    &:lang(ru) {
      min-width: 650px;
    }
  }

  :deep(.table-header .table-cell__inner) {
    justify-content: flex-start !important;
  }

  :deep(.table-header .table-cell),
  :deep(.table-body .table-row .table-cell) {
    padding: 4px 0 !important;
    height: auto;
    text-align: left !important;
    vertical-align: middle;
  }

  :deep(.table-body .table-row .table-cell) {
    height: 26px !important;
  }

  :deep(.table-header .table-cell:nth-child(1)),
  :deep(.table-body .table-row .table-cell:nth-child(1)) {
    padding-right: 5px !important;

    > div {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-body .table-row .table-cell:nth-child(2)) {
    padding-right: 5px !important;
  }

  :deep(.table-header .table-cell:nth-child(3)),
  :deep(.table-body .table-row .table-cell:nth-child(3)) {
    width: 250px;
    padding-right: 5px !important;
    line-height: 18px;

    i {
      line-height: 16px;
    }
  }

  :deep(.table-header .table-cell:nth-child(3):lang(ru)),
  :deep(.table-body .table-row .table-cell:nth-child(3):lang(ru)) {
    width: 370px;
  }

  :deep(.table-header .table-cell:nth-child(3):lang(es)),
  :deep(.table-body .table-row .table-cell:nth-child(3):lang(es)) {
    width: 265px;
  }
}

.main-table__action-buttons {
  display: flex;
  justify-content: flex-end;
}

.main-table__action-button {
  display: flex;
  align-items: center;
  color: var(--color-green);
  margin-left: 16px;
  white-space: nowrap;
}

.values {
  display: flex;
  flex-wrap: wrap;
}

.value {
  padding: 6px 8px;
  background: var(--color-gray5);
  border-radius: 4px;
  margin: 0 8px 4px 0;
}

.archived {
  opacity: 0.6;
}

.show-archived {
  display: flex;
  justify-content: flex-end;
  margin: 0 5px -10px 0;
}
</style>
